import React, { useState, useEffect } from 'react';
import { 
  FlaskConical, Play, RefreshCw, Trash2, Bug, MessageSquare,
  Bot, Workflow, Bell, BarChart3, CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAuth, API } from '../../App';
import { toast } from 'sonner';
import axios from 'axios';

const TestLabPage = () => {
  const { token } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [loading, setLoading] = useState(true);
  const [injecting, setInjecting] = useState(false);
  const [clearing, setClearing] = useState(false);
  
  // Test message form
  const [customerPhone, setCustomerPhone] = useState('+1555123456');
  const [customerName, setCustomerName] = useState('Test Customer');
  const [messageText, setMessageText] = useState('Hi, I need help with my order #12345');
  const [runAIPipeline, setRunAIPipeline] = useState(true);
  const [runWorkflows, setRunWorkflows] = useState(true);
  
  // Results
  const [lastResult, setLastResult] = useState(null);
  const [debugLogs, setDebugLogs] = useState([]);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrg) {
      fetchDebugLogs();
    }
  }, [selectedOrg]);

  const fetchOrganizations = async () => {
    try {
      const response = await axios.get(`${API}/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizations(response.data);
      if (response.data.length > 0) {
        setSelectedOrg(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDebugLogs = async () => {
    try {
      const response = await axios.get(`${API}/test-lab/debug-logs`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { org_id: selectedOrg, limit: 50 }
      });
      setDebugLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch debug logs:', error);
    }
  };

  const handleInjectMessage = async () => {
    if (!selectedOrg || !messageText.trim()) {
      toast.error('Please select an organization and enter a message');
      return;
    }

    setInjecting(true);
    try {
      const response = await axios.post(`${API}/test-lab/inject-message`, {
        organization_id: selectedOrg,
        customer_phone: customerPhone,
        customer_name: customerName,
        message_text: messageText,
        run_ai_pipeline: runAIPipeline,
        run_workflows: runWorkflows
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLastResult(response.data);
      toast.success('Test message injected successfully!');
      fetchDebugLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to inject message');
    } finally {
      setInjecting(false);
    }
  };

  const handleClearOrgData = async () => {
    if (!selectedOrg) return;
    if (!window.confirm('This will delete all conversations and messages for this organization. Continue?')) return;

    setClearing(true);
    try {
      const response = await axios.delete(`${API}/test-lab/clear-org-data/${selectedOrg}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Cleared ${response.data.conversations_deleted} conversations and ${response.data.messages_deleted} messages`);
      setLastResult(null);
      fetchDebugLogs();
    } catch (error) {
      toast.error('Failed to clear data');
    } finally {
      setClearing(false);
    }
  };

  const getLogTypeColor = (type) => {
    switch (type) {
      case 'ai_success': return 'bg-emerald-500';
      case 'ai_error': return 'bg-red-500';
      case 'ai_parse_error': return 'bg-orange-500';
      case 'workflow_executed': return 'bg-blue-500';
      case 'workflow_error': return 'bg-red-500';
      case 'test_lab_inject': return 'bg-purple-500';
      case 'webhook_error': return 'bg-red-500';
      default: return 'bg-slate-500';
    }
  };

  const getLogTypeIcon = (type) => {
    if (type.includes('ai')) return Bot;
    if (type.includes('workflow')) return Workflow;
    if (type.includes('webhook')) return MessageSquare;
    return Bug;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="test-lab-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-purple-600" />
            Test Lab
          </h1>
          <p className="text-muted-foreground">Inject test messages and debug the full pipeline</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={selectedOrg} onValueChange={setSelectedOrg}>
            <SelectTrigger className="w-64" data-testid="org-selector">
              <SelectValue placeholder="Select organization" />
            </SelectTrigger>
            <SelectContent>
              {organizations.map(org => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            variant="destructive" 
            onClick={handleClearOrgData}
            disabled={clearing || !selectedOrg}
            data-testid="clear-data-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            {clearing ? 'Clearing...' : 'Clear Org Data'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Message Injection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Inject Test Message
            </CardTitle>
            <CardDescription>Simulate an incoming WhatsApp message</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer Phone</Label>
                <Input
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="+1555123456"
                  data-testid="customer-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>Customer Name</Label>
                <Input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Test Customer"
                  data-testid="customer-name"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message Text</Label>
              <Textarea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                placeholder="Enter the test message..."
                rows={3}
                data-testid="message-text"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-2">
                <Switch 
                  checked={runAIPipeline} 
                  onCheckedChange={setRunAIPipeline}
                  data-testid="run-ai-toggle"
                />
                <Label className="cursor-pointer">Run AI Pipeline</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch 
                  checked={runWorkflows} 
                  onCheckedChange={setRunWorkflows}
                  data-testid="run-workflows-toggle"
                />
                <Label className="cursor-pointer">Execute Workflows</Label>
              </div>
            </div>

            <Button 
              onClick={handleInjectMessage}
              disabled={injecting || !selectedOrg}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="inject-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {injecting ? 'Injecting...' : 'Inject Message & Run Pipeline'}
            </Button>
          </CardContent>
        </Card>

        {/* Last Result */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Pipeline Result
            </CardTitle>
            <CardDescription>Results from the last test injection</CardDescription>
          </CardHeader>
          <CardContent>
            {lastResult ? (
              <div className="space-y-4">
                {/* Conversation */}
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="font-medium">Conversation</span>
                    <Badge variant={lastResult.conversation?.status === 'created' ? 'default' : 'secondary'}>
                      {lastResult.conversation?.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground font-mono">{lastResult.conversation?.id}</p>
                </div>

                {/* AI Processing */}
                {lastResult.ai_processing && (
                  <div className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="w-4 h-4" />
                      <span className="font-medium">AI Processing</span>
                      {lastResult.ai_processing.error ? (
                        <Badge variant="destructive">Error</Badge>
                      ) : (
                        <Badge className="bg-emerald-600">Success</Badge>
                      )}
                    </div>
                    {lastResult.ai_processing.error ? (
                      <p className="text-xs text-red-500">{lastResult.ai_processing.error}</p>
                    ) : (
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">Intent:</span> {lastResult.ai_processing.intent}</div>
                        <div><span className="text-muted-foreground">Sentiment:</span> {lastResult.ai_processing.sentiment}</div>
                        <div><span className="text-muted-foreground">Language:</span> {lastResult.ai_processing.language}</div>
                        <div><span className="text-muted-foreground">Urgency:</span> {lastResult.ai_processing.urgency}</div>
                      </div>
                    )}
                    {lastResult.ai_processing.suggested_reply && (
                      <div className="mt-2 p-2 bg-emerald-50 dark:bg-emerald-900/20 rounded text-sm">
                        <span className="text-xs text-muted-foreground block mb-1">Suggested Reply:</span>
                        {lastResult.ai_processing.suggested_reply}
                      </div>
                    )}
                  </div>
                )}

                {/* Workflows */}
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Workflow className="w-4 h-4" />
                    <span className="font-medium">Workflows Executed</span>
                    <Badge variant="outline">{lastResult.workflows_executed?.length || 0}</Badge>
                  </div>
                  {lastResult.workflows_executed?.map((wf, i) => (
                    <div key={i} className="text-xs text-muted-foreground">
                      {wf.workflow_name}: {wf.status}
                    </div>
                  ))}
                </div>

                {/* Notifications */}
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    <span className="font-medium">Notifications Created</span>
                    <Badge variant="outline">{lastResult.notifications_created?.length || 0}</Badge>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FlaskConical className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No test results yet</p>
                <p className="text-sm">Inject a message to see pipeline results</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Debug Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bug className="w-5 h-5" />
              Debug Logs
            </CardTitle>
            <CardDescription>Real-time logs for AI requests, responses, and errors</CardDescription>
          </div>
          <Button variant="outline" onClick={fetchDebugLogs} data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-96">
            {debugLogs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No debug logs yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {debugLogs.slice().reverse().map((log) => {
                  const LogIcon = getLogTypeIcon(log.type);
                  return (
                    <div key={log.id} className="p-3 border rounded-lg text-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${getLogTypeColor(log.type)}`} />
                        <LogIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="font-mono text-xs">{log.type}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <pre className="text-xs bg-muted p-2 rounded overflow-x-auto max-h-40">
                        {JSON.stringify(log.data, null, 2)}
                      </pre>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestLabPage;
